/*
 * @format
 */

/* eslint no-unused-vars: ["error", { "argsIgnorePattern": "^_" }] */
/* eslint no-continue: 0 */

import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  race,
  select,
  take,
} from 'redux-saga/effects';

import deepmerge from 'deepmerge';

import * as types from '../action-types';
import * as selectors from '../controls-selectors';
import { MODES } from '../utils/consts';
import { GLOBAL_TIMEOUT } from '../utils/timeout';

const ackTypes = [
  types.WEBSOCKET_SET_PRT_MODE_RECEIVED,
  types.WEBSOCKET_SET_HOLIDAY_MODE_RECEIVED,
];

const rejectTypes = [
  types.WEBSOCKET_SET_PRT_MODE_REJECTED,
  types.WEBSOCKET_SET_HOLIDAY_MODE_REJECTED,
];

const responseTypes = [...ackTypes, ...rejectTypes];

// returns a boolean based on whether the prt state from the last websocket live
// message matches the expected state after the app sends an update (mode change, holiday etc.)
export const doesCurrentStateMatchExpectedState = (
  currentState,
  expectedState,
) =>
  (expectedState.mode === undefined ||
    expectedState.mode === currentState.mode) &&
  (expectedState.setpoint === undefined ||
    expectedState.setpoint === currentState.setpoint) &&
  (expectedState.holidayMode === undefined ||
    expectedState.holidayMode === currentState.holidayMode);

function* sendAndWaitForResponse(action) {
  yield put({ type: types.COMMAND_SENT, message: action.message });
  yield call(action.sendWsMessage, action.message);

  const { endpointId, homeId, message, zoneId: prtId, expectedState } = action;
  const zoneId = [prtId, endpointId];

  let waitingForMatchingLive = true;
  let waitingForAck = true;
  let result;

  while (waitingForMatchingLive || waitingForAck) {
    const newMessage = yield take([
      // match on ack, or nak, where the request id matches, or a LIVE message
      a =>
        responseTypes.find(x => x === a.type) &&
        a.data.requestId === action.message.data.requestId,
      types.WEBSOCKET_LIVE_RECEIVED,
    ]);

    if (newMessage.type === types.WEBSOCKET_LIVE_RECEIVED) {
      if (waitingForMatchingLive) {
        const currentState = yield select(
          selectors.getLastReportedZoneData,
          homeId,
          zoneId,
        );

        waitingForMatchingLive = !doesCurrentStateMatchExpectedState(
          currentState,
          expectedState,
        );

        console.log('Mode change test', {
          expectedState,
          currentState,
          waitingForMatchingLive,
        });
      }
    } else {
      result = newMessage;
      waitingForAck = false;
    }
  }

  if (result && rejectTypes.find(x => x === result.type)) {
    console.log('Got a rejection', action, message);
  }

  // return the ack/nak action
  return result;
}

function* sendWithTimeout(action) {
  const { timeout, result } = yield race({
    timeout: delay(action.timeoutOverride || GLOBAL_TIMEOUT),
    result: call(sendAndWaitForResponse, action),
  });

  if (timeout) {
    yield put(action.timeoutAction);
    console.log('Saga timeout waiting for response', action);
  }

  // if it's not a rejection, flag the next message for sending
  if (!result || !rejectTypes.find(x => x === result.type)) {
    yield put({ ...action, type: types.CLEAR_TO_SEND });
  } else {
    console.log('Sending Reset queue', action);
    yield put({ ...action, type: types.RESET_QUEUE });
  }
}

const getHomeQueueState = (state, homeId) => {
  const home = state[homeId];

  if (!home) {
    return {};
  }

  return home;
};

const getHomeActions = (state, homeId) => {
  const { nextActions } = getHomeQueueState(state, homeId);

  return nextActions || [];
};

const overwriteMerge = (_destinationArray, sourceArray, _options) =>
  sourceArray;

export const assignDebounceType = action => {
  let debounceType = 1;
  if (
    action.message.action === 'cancelOverride' ||
    action.message.action === 'setHolidayMode' ||
    action.message.action === 'cancelHoliday' ||
    action.message.action === 'hpiCancelOverride' ||
    action.message.action === 'hpiSetHolidayMode' ||
    action.message.action === 'hpiCancelHoliday'
  ) {
    debounceType = 3;
  }

  if (
    action.message.data.mode === MODES.OverrideNext ||
    action.message.data.mode === MODES.OverridePlusHours
  ) {
    debounceType = 2;
  }

  return {
    ...action,
    debounceType,
  };
};

export const nextActionsReducer = (nextActions, newAction) => {
  const actions = [...nextActions, newAction];
  const actionsWithDebounceType = actions.map(x => assignDebounceType(x));

  // this could be written with less lines of code but felt
  // it was important to make each case well defined
  const result = actionsWithDebounceType.reduceRight((acc, action) => {
    // first action
    if (acc.length === 0) {
      return [action];
    }

    // cancel override is sticky
    if (action.debounceType === 3) {
      return [action, ...acc];
    }

    // cancel override doesn't overwrite override mode
    if (action.debounceType === 2 && acc[0].debounceType === 3) {
      return [action, ...acc];
    }

    // modes with lower debounce mode are overwritten by next action
    if (action.debounceType <= acc[0].debounceType) {
      return acc;
    }

    return acc;
  }, []);

  return result;
};

// forwards a command from the watch queue to the process queue
function* sendQueue(action) {
  yield put({
    ...action,
    type: types.SEND_PRT_STATE_CHANGE,
  });
}

// queue that listens for both debounced and non-debounced commands
function* watchQueue() {
  while (true) {
    let action = yield take([types.DEBOUNCED_SEND_CONTROL, types.SEND_CONTROL]);

    if (action.type === types.SEND_CONTROL) {
      yield fork(sendQueue, action);
      continue;
    }

    while (true) {
      const { debounced, latestAction } = yield race({
        debounced: delay(1000),
        latestAction: take([types.DEBOUNCED_SEND_CONTROL, types.SEND_CONTROL]),
      });

      // if a non-debounced command comes in, fire off the debounce command first
      if (latestAction && latestAction.type === types.SEND_CONTROL) {
        yield fork(sendQueue, action);
        yield fork(sendQueue, latestAction);
        break;
      }

      if (debounced) {
        yield fork(sendQueue, action);
        break;
      }

      action = latestAction;
    }
  }
}

// Commands should have been sent in the correct order to the process queue.
// It will fire off the websocket messages and determine the next message to send.
function* processQueue() {
  let data = {};
  //  {
  //    'homeId': {
  //      nextActions: [],
  //      task: null,
  //    }
  //  }

  while (true) {
    const action = yield take([
      types.SEND_PRT_STATE_CHANGE,
      types.CLEAR_TO_SEND,
      types.RESET_QUEUE,
    ]);

    const { homeId, zoneId, endpointId } = action;

    if (action.type === types.SEND_PRT_STATE_CHANGE) {
      const endpointActions = getHomeActions(data, homeId);
      // set the queued item
      data = deepmerge(
        data,
        {
          [homeId]: {
            nextActions: nextActionsReducer(endpointActions, action),
          },
        },
        { arrayMerge: overwriteMerge },
      );
    }

    if (action.type === types.RESET_QUEUE) {
      // wipe the state, then NO_SENDS_IN_PROGRESS will be sent and pending state reset
      data = {};
    }

    const { nextActions, task } = getHomeQueueState(data, homeId);

    // is there a queued action, but nothing running at the moment,
    // or we've received a clear to run action
    if (nextActions && nextActions.length > 0) {
      if (action.type === types.CLEAR_TO_SEND || !task || !task.isRunning()) {
        // start a new parallel process to do the sending
        const newTask = yield fork(sendWithTimeout, nextActions[0]);

        data = deepmerge(
          data,
          {
            [homeId]: {
              task: newTask,
              nextActions: nextActions.slice(1, nextActions.length),
            },
          },
          {
            arrayMerge: overwriteMerge,
          },
        );
      }
    } else {
      // must have been a clear to send message, as it didn't bring a new message to send
      // so the queue must be empty
      yield put({
        type: types.NO_SENDS_IN_PROGRESS,
        homeId,
        zoneId,
        endpointId,
      });
    }
  }
}

function* delaySendClear(action) {
  const { homeId, zoneId } = action;

  // if another command has come in don't clear the pending state
  const { timeout, commandReceived } = yield race({
    timeout: delay(7000),
    commandReceived: take([types.DEBOUNCED_SEND_CONTROL, types.SEND_CONTROL]),
  });

  if (timeout) {
    yield put({
      ...action,
      type: types.CLEAR_PENDING_STATE,
    });
  } else if (commandReceived) {
    const { homeId: commandHomeId, zoneId: commandZoneId } = commandReceived;

    // if the command is for a different home/zone clear the pending state
    if (commandHomeId !== homeId || commandZoneId !== zoneId) {
      yield put({
        ...action,
        type: types.CLEAR_PENDING_STATE,
      });
    }
  }
}

function* clearQueue() {
  let pendingTasks = {};
  //  {
  //    'homeId': {
  //      task: null,
  //    }
  //  }

  while (true) {
    const action = yield take(types.NO_SENDS_IN_PROGRESS);

    const { homeId } = action;

    const { task } = getHomeQueueState(pendingTasks, homeId);

    if (task) {
      yield cancel(task);
    }

    const newTask = yield fork(delaySendClear, action);

    pendingTasks = deepmerge(
      pendingTasks,
      {
        [homeId]: {
          task: newTask,
        },
      },
      {
        arrayMerge: overwriteMerge,
      },
    );
  }
}

function* clearLastCommand(action) {
  yield delay(7000);
  yield put({
    ...action,
    type: types.CLEAR_LAST_COMPLETED_COMMAND,
  });
}

function* watchCancelOverrideComplete(sentAction) {
  while (true) {
    const completedAction = yield take(types.COMMAND_COMPLETED);

    if (
      sentAction.requestId === completedAction.requestId &&
      sentAction.message.action === 'cancelOverride'
    ) {
      yield fork(clearLastCommand, sentAction);
    } else {
      break;
    }
  }
}

function* watchCancelOverride() {
  while (true) {
    const sentAction = yield take(types.COMMAND_SENT);

    yield fork(watchCancelOverrideComplete, sentAction);
  }
}

function* control() {
  yield all([
    // the watch queue is what is monitoring for new actions from the user, so is the
    // saga entry point
    call(watchQueue),
    call(processQueue),
    call(clearQueue),
    call(watchCancelOverride),
  ]);
}

export default control;
